/**
 * PhoneScreen — Smartphone overlay with 4 apps:
 *   - Teams (chat con Edu, Fede, Mamá)
 *   - Waze (mini-mapa del mundo)
 *   - Mail (bandeja de entrada)
 *   - Safari (navegador con mundofede.com)
 *
 * Opens when player presses Y (or right-clicks the phone item in hotbar).
 * Shared content lives in PhoneApps.js so the Computer can reuse it.
 */

import { getPhoneApps } from './PhoneApps.js';

export class PhoneScreen {
  constructor({ world, player } = {}) {
    this.world = world;
    this.player = player;
    this.isOpen = false;
    this.currentApp = null;

    this._buildOverlay();
    document.body.appendChild(this.overlay);
  }

  _buildOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'phone-overlay';
    this.overlay.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(0, 0, 0, 0.75);
      z-index: 950;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: default;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;

    // Phone body (iPhone-ish shape)
    this.phone = document.createElement('div');
    this.phone.style.cssText = `
      width: 340px;
      height: 680px;
      background: #1A1A1C;
      border-radius: 44px;
      padding: 12px;
      box-shadow: 0 20px 80px rgba(0,0,0,0.6), inset 0 0 0 2px #2A2A2C;
      position: relative;
    `;
    this.overlay.appendChild(this.phone);

    // Notch
    const notch = document.createElement('div');
    notch.style.cssText = `
      position: absolute;
      top: 12px; left: 50%;
      transform: translateX(-50%);
      width: 120px; height: 28px;
      background: #1A1A1C;
      border-radius: 0 0 18px 18px;
      z-index: 2;
    `;
    this.phone.appendChild(notch);

    // Screen
    this.screen = document.createElement('div');
    this.screen.style.cssText = `
      width: 100%; height: 100%;
      border-radius: 34px;
      overflow: hidden;
      background: #000;
      position: relative;
    `;
    this.phone.appendChild(this.screen);

    // Status bar (always visible)
    this.statusBar = document.createElement('div');
    this.statusBar.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0;
      height: 44px; padding: 12px 32px 0 32px;
      display: flex; justify-content: space-between;
      align-items: center;
      color: white;
      font-size: 13px; font-weight: 600;
      z-index: 3;
      pointer-events: none;
    `;
    this.statusBar.innerHTML = `
      <span class="phone-clock">9:41</span>
      <span>5G  100%</span>
    `;
    this.screen.appendChild(this.statusBar);

    // Content area (apps swap here)
    this.content = document.createElement('div');
    this.content.style.cssText = `
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      overflow: hidden;
    `;
    this.screen.appendChild(this.content);

    // Home indicator bar
    const homeBar = document.createElement('div');
    homeBar.style.cssText = `
      position: absolute;
      bottom: 8px; left: 50%;
      transform: translateX(-50%);
      width: 134px; height: 5px;
      background: white;
      border-radius: 3px;
      z-index: 4;
      cursor: pointer;
    `;
    homeBar.title = 'Home';
    homeBar.onclick = () => this._showHome();
    this.screen.appendChild(homeBar);

    // Close button (only shown on home, top-right outside the screen)
    this.closeBtn = document.createElement('button');
    this.closeBtn.textContent = '×';
    this.closeBtn.style.cssText = `
      position: absolute;
      top: -10px; right: -10px;
      width: 36px; height: 36px;
      border-radius: 50%;
      background: #FF453A; color: white;
      border: 2px solid #1A1A1C;
      font-size: 22px; font-weight: bold;
      cursor: pointer;
      z-index: 10;
    `;
    this.closeBtn.onclick = () => this.close();
    this.phone.appendChild(this.closeBtn);
  }

  _showHome() {
    this.currentApp = null;
    this.content.innerHTML = '';

    // Wallpaper background
    const wallpaper = document.createElement('div');
    wallpaper.style.cssText = `
      position: absolute; inset: 0;
      background: linear-gradient(160deg, #7AB7E8 0%, #2C5F8D 60%, #14304A 100%);
    `;
    this.content.appendChild(wallpaper);

    // Date + time large
    const dateBox = document.createElement('div');
    dateBox.style.cssText = `
      position: absolute; top: 60px; left: 0; right: 0;
      text-align: center; color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.3);
    `;
    dateBox.innerHTML = `
      <div style="font-size:15px; font-weight:500;">domingo 25 de mayo</div>
      <div style="font-size:72px; font-weight:200; line-height:1;">9:41</div>
    `;
    this.content.appendChild(dateBox);

    // App grid (4 apps in 2x2)
    const grid = document.createElement('div');
    grid.style.cssText = `
      position: absolute;
      bottom: 60px; left: 0; right: 0;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      padding: 0 24px;
    `;
    this.content.appendChild(grid);

    const apps = getPhoneApps();
    for (const app of apps) {
      const icon = document.createElement('div');
      icon.style.cssText = `
        display: flex; flex-direction: column;
        align-items: center; cursor: pointer;
      `;
      icon.innerHTML = `
        <div style="
          width: 56px; height: 56px;
          background: ${app.iconBg};
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 28px; font-weight: bold;
          box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        ">${app.iconText}</div>
        <div style="
          margin-top: 6px; color: white;
          font-size: 11px; font-weight: 500;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
        ">${app.name}</div>
      `;
      icon.onclick = () => this._openApp(app);
      grid.appendChild(icon);
    }
  }

  _openApp(app) {
    this.currentApp = app.id;
    this.content.innerHTML = '';

    const appView = document.createElement('div');
    appView.style.cssText = `
      position: absolute; inset: 0;
      background: ${app.bg || '#FFFFFF'};
      padding-top: 44px;
      overflow-y: auto;
    `;
    appView.innerHTML = app.render({ world: this.world, player: this.player });
    this.content.appendChild(appView);

    if (app.onMount) app.onMount(appView, { world: this.world, player: this.player });
  }

  open() {
    this.isOpen = true;
    this.overlay.style.display = 'flex';
    this._showHome();
  }

  close() {
    this.isOpen = false;
    this.overlay.style.display = 'none';
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }
}
